import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/sections/Footer";
import { Users, Target, LineChart, ShieldCheck, ArrowRight, CheckCircle, BarChart3, Award, Clock, ClipboardList } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { FaqSection } from "@/components/services/cybersecurity/FaqSection";
import { ProcessStep } from "@/components/services/cybersecurity/ProcessStep";
import { HeroSection } from "@/components/services/leadership/HeroSection";

const CybersecurityLeadership = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="relative">
        {/* Hero Section */}
        <HeroSection />

        {/* Key Challenges Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                The Security Leadership Challenges You Face
              </h2>
              <p className="text-xl text-gray-600">
                Today's organizations struggle with security leadership in complex and changing environments.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              <div className="bg-gradient-to-br from-purple-50 to-white p-6 rounded-xl border border-purple-100">
                <div className="mb-5 w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <Target className="h-6 w-6 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Changing Regulations</h3>
                <p className="text-gray-600">
                  Keeping up with evolving compliance requirements like GDPR, CCPA, CMMC, and industry-specific regulations is overwhelming.
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-white p-6 rounded-xl border border-purple-100">
                <div className="mb-5 w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <Users className="h-6 w-6 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Organizational Complexity</h3>
                <p className="text-gray-600">
                  Balancing security needs across diverse business units, technology stacks, and stakeholder requirements creates friction.
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-white p-6 rounded-xl border border-purple-100">
                <div className="mb-5 w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <LineChart className="h-6 w-6 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Security vs. Business</h3>
                <p className="text-gray-600">
                  Finding the balance between robust security controls and business agility can seem like a zero-sum game.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* How We Help Section */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-16">
              <span className="inline-block px-4 py-1.5 bg-purple-100 text-purple-800 rounded-full text-sm font-medium mb-5">Our Approach</span>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-gray-900">
                How We Transform Your Security Leadership
              </h2>
              <p className="text-xl text-gray-600">
                We don't just implement controls—we build robust security programs that align with your business objectives and create lasting value.
              </p>
            </div>
            
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                {/* Leadership Development */}
                <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-100">
                  <div className="mb-6 w-14 h-14 rounded-full bg-purple-100 flex items-center justify-center">
                    <Users className="h-7 w-7 text-purple-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Security Leadership Development</h3>
                  <p className="text-gray-600 mb-6">
                    We build security leadership capabilities within your organization through:
                  </p>
                  
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-gray-800 font-medium">Executive-Level Security Workshops</p>
                        <p className="text-gray-600 text-sm mt-1">
                          Tailored sessions that help your leadership team understand security risks in business terms and make informed decisions.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-gray-800 font-medium">Security Steering Committee Design</p>
                        <p className="text-gray-600 text-sm mt-1">
                          Structured approach to creating a cross-functional security governance body with clear roles and responsibilities.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-gray-800 font-medium">Practical Tabletop Exercises</p>
                        <p className="text-gray-600 text-sm mt-1">
                          Scenario-based training that prepares your team to respond effectively during actual security incidents.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="pt-6 mt-6 border-t border-gray-100">
                    <h4 className="text-gray-900 font-semibold mb-3">How This Transforms Your Business:</h4>
                    <p className="text-gray-600 italic">
                      "edgefi helped us create a security leadership structure that aligned our IT, legal, and operations teams. Now we make better security decisions faster." 
                      <span className="text-sm not-italic">— COO, Regional Healthcare Provider</span>
                    </p>
                  </div>
                </div>
                
                {/* Governance Framework */}
                <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-100">
                  <div className="mb-6 w-14 h-14 rounded-full bg-purple-100 flex items-center justify-center">
                    <ClipboardList className="h-7 w-7 text-purple-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Security Governance Frameworks</h3>
                  <p className="text-gray-600 mb-6">
                    We implement practical governance frameworks that provide structure without bureaucracy:
                  </p>
                  
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-gray-800 font-medium">Tailored Policies & Procedures</p>
                        <p className="text-gray-600 text-sm mt-1">
                          Right-sized documentation that's actually usable—not overwhelming templates that gather digital dust.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-gray-800 font-medium">Security Decision Framework</p>
                        <p className="text-gray-600 text-sm mt-1">
                          A structured approach for making risk-based security decisions that balances protection with business needs.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-gray-800 font-medium">Security Metrics & Reporting</p>
                        <p className="text-gray-600 text-sm mt-1">
                          Meaningful KPIs that demonstrate security program effectiveness to both technical and non-technical stakeholders.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="pt-6 mt-6 border-t border-gray-100">
                    <h4 className="text-gray-900 font-semibold mb-3">How This Transforms Your Business:</h4>
                    <p className="text-gray-600 italic">
                      "The governance framework edgefi implemented cut our audit preparation time in half while giving us better visibility into our security posture." 
                      <span className="text-sm not-italic">— IT Director, Financial Services Firm</span>
                    </p>
                  </div>
                </div>
                
                {/* Compliance Management */}
                <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-100">
                  <div className="mb-6 w-14 h-14 rounded-full bg-purple-100 flex items-center justify-center">
                    <ShieldCheck className="h-7 w-7 text-purple-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Integrated Compliance Management</h3>
                  <p className="text-gray-600 mb-6">
                    We help you handle multiple compliance requirements efficiently:
                  </p>
                  
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-gray-800 font-medium">Unified Control Framework</p>
                        <p className="text-gray-600 text-sm mt-1">
                          Map controls across different frameworks (NIST, ISO, SOC 2, HIPAA, etc.) to minimize duplicative efforts.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-gray-800 font-medium">Evidence Collection Automation</p>
                        <p className="text-gray-600 text-sm mt-1">
                          Streamlined processes to gather and maintain compliance evidence with minimal disruption to your team.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-gray-800 font-medium">Readiness Assessments & Gap Analysis</p>
                        <p className="text-gray-600 text-sm mt-1">
                          Detailed evaluations of your current compliance status with actionable remediation plans.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="pt-6 mt-6 border-t border-gray-100">
                    <h4 className="text-gray-900 font-semibold mb-3">How This Transforms Your Business:</h4>
                    <p className="text-gray-600 italic">
                      "With edgefi's help, we achieved SOC 2 compliance in just 5 months—half the time we expected—while also meeting our GDPR requirements." 
                      <span className="text-sm not-italic">— CEO, SaaS Startup</span>
                    </p>
                  </div>
                </div>
                
                {/* Risk Management */}
                <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-100">
                  <div className="mb-6 w-14 h-14 rounded-full bg-purple-100 flex items-center justify-center">
                    <BarChart3 className="h-7 w-7 text-purple-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Practical Risk Management</h3>
                  <p className="text-gray-600 mb-6">
                    We implement risk management that drives action, not just documentation:
                  </p>
                  
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-gray-800 font-medium">Business-Aligned Risk Assessment</p>
                        <p className="text-gray-600 text-sm mt-1">
                          Risk evaluations that focus on business impact, not just technical vulnerabilities.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-gray-800 font-medium">Risk Appetite Definition</p>
                        <p className="text-gray-600 text-sm mt-1">
                          Clear guidelines for determining acceptable risk levels based on your organization's objectives.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-gray-800 font-medium">Risk Treatment Roadmaps</p>
                        <p className="text-gray-600 text-sm mt-1">
                          Prioritized, actionable plans to address identified risks within your resource constraints.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="pt-6 mt-6 border-t border-gray-100">
                    <h4 className="text-gray-900 font-semibold mb-3">How This Transforms Your Business:</h4>
                    <p className="text-gray-600 italic">
                      "edgefi's risk management approach helped us focus our limited security budget on the controls that truly matter to our business." 
                      <span className="text-sm not-italic">— CFO, Manufacturing Company</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Our Process Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-16">
              <span className="inline-block px-4 py-1.5 bg-purple-100 text-purple-800 rounded-full text-sm font-medium mb-5">Our Methodology</span>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-gray-900">
                Proven Process for Building Security Leadership
              </h2>
              <p className="text-xl text-gray-600">
                Our systematic approach ensures we deliver consistent results, tailored to your organization's unique needs.
              </p>
            </div>
            
            <div className="max-w-5xl mx-auto">
              <div className="relative">
                {/* Vertical line */}
                <div className="absolute left-6 top-8 bottom-8 w-0.5 bg-purple-200 hidden sm:block"></div>
                
                <div className="space-y-12">
                  <ProcessStep 
                    number={1}
                    title="Discovery & Assessment"
                    description="We start by understanding your business objectives, current security posture, and compliance requirements through:"
                    bulletPoints={[
                      "Stakeholder interviews across business functions",
                      "Documentation review of existing policies and procedures",
                      "Gap analysis against relevant frameworks and regulations"
                    ]}
                    testimonial="The discovery process was thorough but efficient. edgefi quickly understood our business and identified security gaps we hadn't recognized."
                  />
                  
                  <ProcessStep 
                    number={2}
                    title="Strategic Planning"
                    description="We develop a tailored security strategy and roadmap that addresses your specific needs:"
                    bulletPoints={[
                      "Prioritized security initiatives based on risk and business impact",
                      "Resource planning to ensure implementation feasibility",
                      "Clear success metrics aligned with business outcomes"
                    ]}
                    testimonial="The roadmap edgefi created was pragmatic—they understood our resource constraints and developed a plan we could actually implement."
                  />
                  
                  <ProcessStep 
                    number={3}
                    title="Implementation & Knowledge Transfer"
                    description="We work alongside your team to implement the security program while building internal capabilities:"
                    bulletPoints={[
                      "Collaborative implementation with your team to ensure knowledge transfer",
                      "Hands-on training and documentation for ongoing program maintenance",
                      "Regular stakeholder updates to maintain visibility and alignment"
                    ]}
                    testimonial="edgefi didn't just 'do it for us'—they built our team's capabilities so we can maintain our security program long-term."
                  />
                  
                  <ProcessStep 
                    number={4}
                    title="Measurement & Continuous Improvement"
                    description="We establish processes to measure effectiveness and continuously improve your security program:"
                    bulletPoints={[
                      "Regular security maturity assessments against established benchmarks",
                      "Feedback loops from security operations to leadership",
                      "Adaptation to evolving threats and business requirements"
                    ]}
                    testimonial="The metrics dashboard edgefi implemented gives our board clear visibility into our security program's effectiveness."
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Results Section */}
        <section className="py-16 bg-gradient-to-b from-purple-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-16">
              <span className="inline-block px-4 py-1.5 bg-purple-100 text-purple-800 rounded-full text-sm font-medium mb-5">Measurable Results</span>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-gray-900">
                Real Business Impact
              </h2>
              <p className="text-xl text-gray-600">
                Our clients consistently achieve tangible business benefits from our security leadership programs.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-12">
              <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-100 text-center">
                <div className="mx-auto mb-5 w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center">
                  <Clock className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="text-4xl font-bold text-purple-600 mb-2">65%</h3>
                <p className="text-xl font-semibold text-gray-900 mb-3">Faster Compliance</p>
                <p className="text-gray-600">
                  Our clients achieve compliance certifications in 65% less time than the industry average.
                </p>
              </div>
              
              <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-100 text-center">
                <div className="mx-auto mb-5 w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center">
                  <Award className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="text-4xl font-bold text-purple-600 mb-2">3x</h3>
                <p className="text-xl font-semibold text-gray-900 mb-3">Win Rate Increase</p>
                <p className="text-gray-600">
                  Clients report up to 3x higher win rates in competitive deals after implementing our security program.
                </p>
              </div>
              
              <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-100 text-center">
                <div className="mx-auto mb-5 w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center">
                  <BarChart3 className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="text-4xl font-bold text-purple-600 mb-2">40%</h3>
                <p className="text-xl font-semibold text-gray-900 mb-3">Cost Reduction</p>
                <p className="text-gray-600">
                  Our integrated compliance approach reduces security and compliance costs by an average of 40%.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Success Stories Section */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl text-center mb-12">
              Success Stories
            </h2>
            
            <div className="grid grid-cols-1 gap-8">
              {/* Restaurant Chain Case Study */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow duration-300">
                <div className="md:grid md:grid-cols-3 gap-6">
                  <div className="p-6 md:col-span-2">
                    <div className="text-sm text-edgefi-purple font-medium mb-2">Case Study</div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      Regional Restaurant Chain Achieves PCI Compliance
                    </h3>
                    <p className="text-gray-600 mb-4">
                      A regional restaurant chain with 45 locations faced growing challenges with PCI compliance management across their distributed environment.
                    </p>
                    <div className="space-y-3 mb-6">
                      <div className="flex items-start gap-2">
                        <div className="h-5 w-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <CheckCircle className="h-3 w-3 text-green-600" />
                        </div>
                        <p className="text-gray-700">Consolidated 4 different compliance frameworks into a unified control set</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="h-5 w-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <CheckCircle className="h-3 w-3 text-green-600" />
                        </div>
                        <p className="text-gray-700">Implemented automated evidence collection across all locations</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="h-5 w-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <CheckCircle className="h-3 w-3 text-green-600" />
                        </div>
                        <p className="text-gray-700">Established a security governance structure with clear accountability</p>
                      </div>
                    </div>
                    <div className="text-gray-600">
                      <p className="font-semibold text-gray-900">Results:</p>
                      <p>Reduced compliance preparation time by 60% and passed their PCI audit without remediation items for the first time.</p>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-indigo-50 p-6 flex flex-col justify-center">
                    <div className="bg-white rounded-xl p-5 shadow-sm">
                      <p className="italic text-gray-600 mb-4">
                        "edgefi transformed our security and compliance program from a constant fire drill to a well-managed process. Their leadership expertise was exactly what we needed."
                      </p>
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-gray-200 flex-shrink-0"></div>
                        <div className="ml-3">
                          <p className="text-sm font-semibold text-gray-900">Sarah M.</p>
                          <p className="text-xs text-gray-500">CIO, Regional Restaurant Group</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* FAQ Section */}
        <FaqSection />
      </main>
      <Footer />
    </div>
  );
};

export default CybersecurityLeadership;
